'use client'

import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  ArrowRight,
  Briefcase,
  Building2,
  CalendarClock,
  ChevronDown,
  FileText,
  GraduationCap,
  Info,
  Mail,
  MapPin,
  Menu,
  MessageCircle,
  Phone,
  Plane,
  ShieldCheck,
  Smile,
  Sparkles,
  Users,
  X,
  type LucideIcon,
} from 'lucide-react'
import { CATEGORIES, COURSES, INTAKE, coursePath } from '@/data/catalog'
import { CourseIcon } from '@/components/course/CourseIcon'
import { LiveClocks } from './LiveClocks'
import { ApplyButton } from '@/components/apply/ApplyButton'

type Locale = 'en' | 'ja'
type MenuId = 'courses' | 'programs' | 'about' | null

const WHATSAPP = 'https://wa.me/923001301223'

const L = {
  en: {
    courses: 'Courses',
    work: 'Work in Japan',
    jlpt: 'JLPT',
    programs: 'Programs',
    about: 'About',
    contact: 'Contact',
    apply: 'Apply Now',
    applyShort: 'Apply',
    allCourses: 'View all courses',
    intakeLine: '2026/27 intake is open',
    intakeCta: 'Seats are limited, apply today',
    menu: 'Menu',
    close: 'Close menu',
    language: 'Language',
    programsItems: [
      { href: '/individuals', title: 'Individuals', desc: 'JLPT N5–N1, SSW & TITP, jobs and visa support' },
      { href: '/kids', title: 'Kids (9–12)', desc: '1-year and 2-year Japanese plans for children' },
      { href: '/business', title: 'Business', desc: 'Corporate training, Japan visits and market research' },
    ],
    aboutItems: [
      { href: '/about', title: 'About NCP', desc: 'Who we are and our two campuses' },
      { href: '/about#instructors', title: 'Instructors', desc: 'Meet the people who teach you' },
      { href: '/careers', title: 'Careers', desc: 'Teach or work with us' },
      { href: '/contact', title: 'Contact', desc: 'Visit, call or message us' },
    ],
  },
  ja: {
    courses: 'コース',
    work: '日本で働く',
    jlpt: 'JLPT',
    programs: 'プログラム',
    about: 'NCPについて',
    contact: 'お問い合わせ',
    apply: '今すぐ申し込む',
    applyShort: '申込',
    allCourses: 'すべてのコースを見る',
    intakeLine: '2026/27年度 受付中',
    intakeCta: '定員に限りがあります',
    menu: 'メニュー',
    close: 'メニューを閉じる',
    language: '言語',
    programsItems: [
      { href: '/individuals', title: '個人', desc: 'JLPT N5〜N1、特定技能・技能実習、就職・ビザ支援' },
      { href: '/kids', title: 'キッズ（9〜12歳）', desc: 'お子さま向け1年・2年プラン' },
      { href: '/business', title: '法人', desc: '企業研修・日本視察・市場調査' },
    ],
    aboutItems: [
      { href: '/about', title: 'NCPについて', desc: '私たちと2つのキャンパス' },
      { href: '/about#instructors', title: '講師紹介', desc: '講師を紹介します' },
      { href: '/careers', title: '採用情報', desc: '一緒に働きませんか' },
      { href: '/contact', title: 'お問い合わせ', desc: '来校・電話・メッセージ' },
    ],
  },
}

type PanelLink = { href: string; label: { en: string; ja: string }; icon: LucideIcon }
type PanelCol = { title: { en: string; ja: string }; jp: string; links: PanelLink[] }

/** Programs and About dropdowns: same layout as the Courses mega menu. */
const PANELS: Record<'programs' | 'about', { cols: PanelCol[]; note: { en: string; ja: string }; cta: { href: string; label: { en: string; ja: string } } }> = {
  programs: {
    cols: [
      {
        title: { en: 'Individuals', ja: '個人' },
        jp: '個人',
        links: [
          { href: '/individuals', label: { en: 'Individuals program', ja: '個人向けプログラム' }, icon: GraduationCap },
          { href: '/individuals#class-schedule', label: { en: 'JLPT batch timings', ja: 'JLPTクラス時間' }, icon: CalendarClock },
          { href: '/individuals#programs', label: { en: 'SSW, TITP, jobs & visa', ja: '特定技能・技能実習・就職・ビザ' }, icon: Briefcase },
          { href: '/work-in-japan', label: { en: 'Work in Japan guide', ja: '日本で働くガイド' }, icon: Plane },
        ],
      },
      {
        title: { en: 'Kids (9–12)', ja: 'キッズ（9〜12歳）' },
        jp: 'キッズ',
        links: [
          { href: '/kids', label: { en: 'Kids program', ja: 'キッズプログラム' }, icon: Smile },
          { href: '/courses/japanese-for-kids', label: { en: 'Japanese for Kids course', ja: 'キッズ日本語コース' }, icon: GraduationCap },
          { href: '/courses/culture-workshops', label: { en: 'Culture workshops', ja: '日本文化ワークショップ' }, icon: Sparkles },
        ],
      },
      {
        title: { en: 'Business', ja: '法人' },
        jp: '法人',
        links: [
          { href: '/business', label: { en: 'Business program', ja: '法人向けプログラム' }, icon: Building2 },
          { href: '/business#services', label: { en: 'Training, research & visits', ja: '研修・調査・視察' }, icon: Briefcase },
          { href: '/courses/business-japanese', label: { en: 'Business Japanese & BJT', ja: 'ビジネス日本語・BJT' }, icon: GraduationCap },
          { href: '/courses/it-japanese', label: { en: 'Japanese for IT Engineers', ja: 'ITエンジニアの日本語' }, icon: GraduationCap },
        ],
      },
    ],
    note: { en: 'Individuals, kids and companies · on campus or online', ja: '個人・キッズ・法人／通学・オンライン' },
    cta: { href: '/courses', label: { en: 'View all courses', ja: 'すべてのコースを見る' } },
  },
  about: {
    cols: [
      {
        title: { en: 'About NCP', ja: 'NCPについて' },
        jp: '会社案内',
        links: [
          { href: '/about', label: { en: 'About us', ja: '私たちについて' }, icon: Info },
          { href: '/about#campuses', label: { en: 'Our campuses', ja: 'キャンパス' }, icon: MapPin },
        ],
      },
      {
        title: { en: 'Our team', ja: 'チーム' },
        jp: '講師',
        links: [
          { href: '/about#instructors', label: { en: 'Instructors', ja: '講師紹介' }, icon: Users },
          { href: '/careers', label: { en: 'Careers', ja: '採用情報' }, icon: Briefcase },
        ],
      },
      {
        title: { en: 'Contact', ja: 'お問い合わせ' },
        jp: '連絡',
        links: [
          { href: '/contact', label: { en: 'Contact form', ja: 'お問い合わせフォーム' }, icon: Mail },
          { href: 'https://wa.me/923001301223', label: { en: 'WhatsApp', ja: 'WhatsApp' }, icon: MessageCircle },
          { href: 'tel:+923001301223', label: { en: 'Call +92 300 1301223', ja: '電話 +92 300 1301223' }, icon: Phone },
        ],
      },
      {
        title: { en: 'Policies', ja: '規約' },
        jp: '規約',
        links: [
          { href: '/privacy-policy', label: { en: 'Privacy Policy', ja: 'プライバシーポリシー' }, icon: ShieldCheck },
          { href: '/terms-of-service', label: { en: 'Terms of Service', ja: '利用規約' }, icon: FileText },
        ],
      },
    ],
    note: { en: 'Gulberg III, Lahore · Alipur Chatha', ja: 'ラホール（グルバーグIII）・アリプル・チャタ' },
    cta: { href: '/contact', label: { en: 'Contact us', ja: 'お問い合わせ' } },
  },
}

function useLocale(pathname: string): Locale {
  return pathname.startsWith('/ja') ? 'ja' : 'en'
}

export function SiteHeader({ phone, email }: { phone: string; email: string }) {
  const pathname = usePathname() ?? '/en'
  const locale = useLocale(pathname)
  const t = L[locale]
  const p = (href: string) => `/${locale}${href}`
  const [open, setOpen] = useState<MenuId>(null)
  const [drawer, setDrawer] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const closeTimer = useRef<number>(0)
  const openedByHover = useRef(0)
  const telHref = `tel:${phone.replace(/[^\d+]/g, '')}`

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // close menus on route change
  useEffect(() => {
    setOpen(null)
    setDrawer(false)
  }, [pathname])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(null)
        setDrawer(false)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  useEffect(() => {
    document.documentElement.style.overflow = drawer ? 'hidden' : ''
    return () => {
      document.documentElement.style.overflow = ''
    }
  }, [drawer])

  const hoverOpen = useCallback((id: MenuId) => {
    window.clearTimeout(closeTimer.current)
    setOpen((cur) => {
      if (cur !== id) openedByHover.current = Date.now()
      return id
    })
  }, [])
  const hoverClose = useCallback(() => {
    window.clearTimeout(closeTimer.current)
    closeTimer.current = window.setTimeout(() => setOpen(null), 160)
  }, [])
  // a click right after hover-open keeps the menu open instead of closing it
  const clickToggle = (id: MenuId) =>
    setOpen((cur) => (cur === id && Date.now() - openedByHover.current > 500 ? null : id))

  const switchLang = (to: Locale) => {
    const rest = pathname.replace(/^\/(en|ja)/, '')
    window.location.href = `/${to}${rest}${window.location.hash}`
  }

  const isActive = (href: string) => {
    const full = p(href)
    return pathname === full || pathname.startsWith(full + '/')
  }
  const coursesActive = isActive('/courses')
  const programsActive = ['/individuals', '/kids', '/business'].some(isActive)
  const aboutActive = ['/about', '/careers', '/contact'].some(isActive)

  const navLink = (active: boolean) =>
    `relative inline-flex h-10 items-center gap-1 whitespace-nowrap rounded-full px-2 text-[13.5px] font-medium transition-colors xl:px-3.5 xl:text-[14.5px] ${
      active ? 'text-brand-green' : 'text-brand-charcoal hover:text-brand-green'
    }`
  const activeBar = (active: boolean) => (
    <span
      aria-hidden
      className={`absolute inset-x-3 -bottom-[3px] h-[2px] rounded-full bg-gradient-to-r from-brand-green via-brand-gold to-brand-red transition-transform duration-300 ${
        active ? 'scale-x-100' : 'scale-x-0'
      }`}
    />
  )

  return (
    <>
      <header
        data-no-reveal
        className={`fixed inset-x-0 top-0 z-50 transition-transform duration-500 ease-premium ${scrolled ? 'md:-translate-y-[34px]' : ''}`}
      >
        {/* ---------- top bar (tablet/desktop) ---------- */}
        <div className="relative hidden h-[34px] overflow-hidden bg-brand-navy text-[12px] text-white/80 md:block">
          <div className="ncp-asanoha absolute inset-0 opacity-60" aria-hidden />
          <div className="relative mx-auto flex h-full max-w-7xl items-center justify-between gap-4 px-4 lg:px-6">
            <LiveClocks locale={locale} />
            <ApplyButton className="group hidden items-center gap-2 text-white lg:inline-flex">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-gold opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-brand-gold" />
              </span>
              <b className="font-semibold">{t.intakeLine}</b>
              <span className="text-white/60">·</span>
              <span className="text-white/70 group-hover:text-brand-gold">{t.intakeCta} →</span>
            </ApplyButton>
            <div className="flex items-center gap-4">
              <a href={telHref} className="hidden items-center gap-1.5 hover:text-brand-gold xl:inline-flex">
                <Phone className="h-3.5 w-3.5" /> {phone}
              </a>
              <a href={`mailto:${email}`} className="hidden items-center gap-1.5 hover:text-brand-gold xl:inline-flex">
                <Mail className="h-3.5 w-3.5" /> {email}
              </a>
              <a href={WHATSAPP} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 hover:text-brand-gold">
                <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
              </a>
              <span className="h-3.5 w-px bg-white/20" aria-hidden />
              <div className="inline-flex rounded-full bg-white/10 p-0.5" role="group" aria-label={t.language}>
                {(['en', 'ja'] as const).map((l) => (
                  <button
                    key={l}
                    type="button"
                    onClick={() => switchLang(l)}
                    aria-pressed={locale === l}
                    className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold transition-colors ${
                      locale === l ? 'bg-white text-brand-navy' : 'text-white/75 hover:text-white'
                    }`}
                  >
                    {l === 'en' ? 'EN' : '日本語'}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ---------- main bar ---------- */}
        <div
          className={`relative border-b transition-all duration-300 ${
            scrolled || open || drawer
              ? 'border-brand-cool-gray/70 bg-white/95 shadow-[0_10px_30px_-18px_rgba(20,33,61,0.35)] backdrop-blur-xl'
              : 'border-transparent bg-white/70 backdrop-blur-md'
          }`}
          onMouseLeave={hoverClose}
        >
          <span aria-hidden className="absolute inset-x-0 bottom-[-1px] h-px bg-gradient-to-r from-brand-green/0 via-brand-gold/70 to-brand-red/0" />
          <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-3 px-4 md:h-[68px] lg:px-6">
            {/* logo */}
            <Link href={`/${locale}`} className="group flex min-w-0 items-center gap-2 sm:gap-2.5" aria-label="Nippon Center Pakistan, home">
              <Image
                src="/ncp-mark.webp"
                alt=""
                width={346}
                height={192}
                priority
                className="h-8 w-auto transition-transform duration-500 group-hover:scale-105 sm:h-9 md:h-11"
              />
              <span className="flex min-w-0 flex-col leading-none lg:hidden xl:flex">
                <span className="whitespace-nowrap text-[13px] font-bold tracking-[-0.01em] text-brand-navy sm:text-[15px] md:text-[16.5px]">
                  Nippon Center <span className="text-brand-green">Pakistan</span>
                </span>
                <span className="mt-1 whitespace-nowrap font-[family-name:var(--font-noto-serif-jp)] text-[9.5px] font-semibold tracking-[0.12em] text-brand-red sm:text-[10.5px] sm:tracking-[0.18em] md:text-[11.5px]">
                  日本センター・パキスタン
                </span>
              </span>
            </Link>

            {/* desktop nav */}
            <nav className="hidden items-center lg:flex" aria-label="Main">
              <div onMouseEnter={() => hoverOpen('courses')} className="relative">
                <Link
                  href={p('/courses')}
                  className={navLink(coursesActive || open === 'courses')}
                  aria-expanded={open === 'courses'}
                  onFocus={() => setOpen('courses')}
                >
                  {t.courses}
                  <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open === 'courses' ? 'rotate-180' : ''}`} />
                  {activeBar(coursesActive)}
                </Link>
              </div>
              <Link href={p('/work-in-japan')} className={navLink(isActive('/work-in-japan'))} onMouseEnter={hoverClose}>
                {t.work}
                {activeBar(isActive('/work-in-japan'))}
              </Link>
              <Link href={p('/jlpt')} className={navLink(isActive('/jlpt'))} onMouseEnter={hoverClose}>
                {t.jlpt}
                {activeBar(isActive('/jlpt'))}
              </Link>
              {(['programs', 'about'] as const).map((id) => {
                const active = id === 'programs' ? programsActive : aboutActive
                return (
                  <div key={id} className="relative" onMouseEnter={() => hoverOpen(id)} onFocus={() => setOpen(id)}>
                    <button
                      type="button"
                      className={navLink(active || open === id)}
                      aria-expanded={open === id}
                      onClick={() => clickToggle(id)}
                    >
                      {id === 'programs' ? t.programs : t.about}
                      <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open === id ? 'rotate-180' : ''}`} />
                      {activeBar(active)}
                    </button>
                  </div>
                )
              })}
              <Link href={p('/contact')} className={navLink(isActive('/contact'))} onMouseEnter={hoverClose}>
                {t.contact}
                {activeBar(isActive('/contact'))}
              </Link>
            </nav>

            {/* actions */}
            <div className="flex shrink-0 items-center gap-2">
              <ApplyButton
                data-cta="header"
                className="ncp-header-cta inline-flex h-10 items-center gap-1 whitespace-nowrap rounded-full px-3 text-sm font-semibold text-white sm:gap-1.5 sm:px-5"
              >
                <span className="sm:hidden">{t.applyShort}</span>
                <span className="hidden sm:inline">{t.apply}</span>
                <ArrowRight className="h-4 w-4" />
              </ApplyButton>
              <button
                type="button"
                onClick={() => setDrawer(true)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-brand-cool-gray bg-white text-brand-navy lg:hidden"
                aria-label={t.menu}
                aria-expanded={drawer}
              >
                <Menu className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* courses mega menu */}
          <div
            className={`absolute inset-x-0 top-full hidden transition-all duration-300 lg:block ${
              open === 'courses' ? 'visible translate-y-0 opacity-100' : 'pointer-events-none invisible -translate-y-2 opacity-0'
            }`}
            onMouseEnter={() => hoverOpen('courses')}
          >
            <div className="mx-auto max-w-7xl px-4 pt-3 lg:px-6">
              <div className="relative overflow-hidden rounded-3xl border border-brand-cool-gray/80 bg-white shadow-[0_40px_80px_-40px_rgba(20,33,61,0.5)]">
                <div className="ncp-seigaiha pointer-events-none absolute inset-x-0 bottom-0 h-20 opacity-40 [mask-image:linear-gradient(to_top,#000,transparent)]" aria-hidden />
                <div className="relative grid grid-cols-5 gap-1 p-4">
                  {CATEGORIES.map((cat) => (
                    <div key={cat.id} className="rounded-2xl p-3">
                      <p className="flex items-baseline justify-between gap-2 border-b border-brand-cool-gray/70 pb-2">
                        <span className="text-[13px] font-bold uppercase tracking-[0.08em] text-brand-navy">{cat.title[locale]}</span>
                        <span className="shrink-0 whitespace-nowrap font-[family-name:var(--font-noto-serif-jp)] text-[11px] text-brand-red/80">{cat.jp}</span>
                      </p>
                      <ul className="mt-2 space-y-0.5">
                        {COURSES.filter((c) => c.category === cat.id).map((c) => (
                          <li key={c.slug}>
                            <Link
                              href={coursePath(locale, c.slug)}
                              className="group flex items-start gap-2 rounded-lg px-2 py-1.5 text-[13px] leading-snug text-brand-charcoal transition-colors hover:bg-brand-soft-gray hover:text-brand-green"
                            >
                              <CourseIcon name={c.icon} className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-gold group-hover:text-brand-green" />
                              <span>{c.title[locale].replace(' Preparation', '').replace(' 対策コース', '')}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
                <div className="relative flex items-center justify-between gap-4 border-t border-brand-cool-gray/70 bg-brand-soft-gray/70 px-7 py-3.5">
                  <p className="text-sm text-brand-charcoal">
                    <b className="text-brand-green">{INTAKE[locale]}</b>
                    <span className="mx-2 text-brand-muted">·</span>
                    {locale === 'ja' ? '通学・オンライン／午前・午後クラス' : 'On campus or online · morning and afternoon batches'}
                  </p>
                  <Link href={p('/courses')} className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-red hover:text-brand-red-deep">
                    {t.allCourses} <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* programs + about menus: same style as the courses mega menu */}
          {(['programs', 'about'] as const).map((id) => {
            const panel = PANELS[id]
            return (
              <div
                key={id}
                className={`absolute inset-x-0 top-full hidden transition-all duration-300 lg:block ${
                  open === id ? 'visible translate-y-0 opacity-100' : 'pointer-events-none invisible -translate-y-2 opacity-0'
                }`}
                onMouseEnter={() => hoverOpen(id)}
              >
                <div className="mx-auto max-w-7xl px-4 pt-3 lg:px-6">
                  <div className="relative overflow-hidden rounded-3xl border border-brand-cool-gray/80 bg-white shadow-[0_40px_80px_-40px_rgba(20,33,61,0.5)]">
                    <div className="ncp-seigaiha pointer-events-none absolute inset-x-0 bottom-0 h-20 opacity-40 [mask-image:linear-gradient(to_top,#000,transparent)]" aria-hidden />
                    <div className={`relative grid gap-1 p-4 ${panel.cols.length === 4 ? 'grid-cols-4' : 'grid-cols-3'}`}>
                      {panel.cols.map((col) => (
                        <div key={col.title.en} className="rounded-2xl p-3">
                          <p className="flex items-baseline justify-between gap-2 border-b border-brand-cool-gray/70 pb-2">
                            <span className="text-[13px] font-bold uppercase tracking-[0.08em] text-brand-navy">{col.title[locale]}</span>
                            <span className="shrink-0 whitespace-nowrap font-[family-name:var(--font-noto-serif-jp)] text-[11px] text-brand-red/80">{col.jp}</span>
                          </p>
                          <ul className="mt-2 space-y-0.5">
                            {col.links.map((l) => {
                              const external = !l.href.startsWith('/')
                              const cls =
                                'group flex items-start gap-2 rounded-lg px-2 py-1.5 text-[13px] leading-snug text-brand-charcoal transition-colors hover:bg-brand-soft-gray hover:text-brand-green'
                              const inner = (
                                <>
                                  <l.icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-gold group-hover:text-brand-green" />
                                  <span>{l.label[locale]}</span>
                                </>
                              )
                              return (
                                <li key={l.href}>
                                  {external ? (
                                    <a href={l.href} className={cls} {...(l.href.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
                                      {inner}
                                    </a>
                                  ) : (
                                    <Link href={p(l.href)} className={cls}>
                                      {inner}
                                    </Link>
                                  )}
                                </li>
                              )
                            })}
                          </ul>
                        </div>
                      ))}
                    </div>
                    <div className="relative flex items-center justify-between gap-4 border-t border-brand-cool-gray/70 bg-brand-soft-gray/70 px-7 py-3.5">
                      <p className="text-sm text-brand-charcoal">
                        <b className="text-brand-green">{INTAKE[locale]}</b>
                        <span className="mx-2 text-brand-muted">·</span>
                        {panel.note[locale]}
                      </p>
                      <Link href={p(panel.cta.href)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-red hover:text-brand-red-deep">
                        {panel.cta.label[locale]} <ArrowRight className="h-4 w-4" />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </header>

      {/* ---------- mobile / tablet drawer ---------- */}
      <div
        className={`fixed inset-0 z-[60] lg:hidden ${drawer ? '' : 'pointer-events-none'}`}
        aria-hidden={!drawer}
        role="dialog"
        aria-modal="true"
        aria-label={t.menu}
      >
        <div
          className={`absolute inset-0 bg-brand-navy/50 backdrop-blur-sm transition-opacity duration-300 ${drawer ? 'opacity-100' : 'opacity-0'}`}
          onClick={() => setDrawer(false)}
        />
        <div
          className={`absolute inset-y-0 right-0 flex w-full max-w-[420px] flex-col bg-[#fbf9f4] shadow-2xl transition-transform duration-500 ease-premium ${
            drawer ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex h-16 items-center justify-between border-b border-brand-cool-gray/70 px-4">
            <span className="flex items-center gap-2">
              <Image src="/ncp-mark.webp" alt="" width={346} height={192} className="h-8 w-auto" />
              <span className="font-[family-name:var(--font-noto-serif-jp)] text-xs font-semibold tracking-[0.18em] text-brand-red">日本センター</span>
            </span>
            <button
              type="button"
              onClick={() => setDrawer(false)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-brand-cool-gray bg-white"
              aria-label={t.close}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-4">
            <ApplyButton onClick={() => setDrawer(false)} className="mb-4 flex w-full items-center justify-between text-left rounded-2xl bg-gradient-to-r from-brand-red to-[#d62a52] px-5 py-4 text-white shadow-lg">
              <span>
                <span className="block text-xs font-medium uppercase tracking-[0.18em] text-white/75">{t.intakeLine}</span>
                <span className="mt-0.5 block text-lg font-bold">{t.apply}</span>
              </span>
              <ArrowRight className="h-6 w-6" />
            </ApplyButton>
            <MobileGroup title={t.courses}>
              {CATEGORIES.map((cat) => (
                <div key={cat.id} className="mb-3">
                  <p className="px-3 pb-1 text-[11px] font-bold uppercase tracking-[0.14em] text-brand-muted">{cat.title[locale]}</p>
                  {COURSES.filter((c) => c.category === cat.id).map((c) => (
                    <Link key={c.slug} href={coursePath(locale, c.slug)} className="flex items-center gap-2.5 rounded-xl px-3 py-2 text-[15px] text-brand-charcoal active:bg-white">
                      <CourseIcon name={c.icon} className="h-4 w-4 text-brand-gold" />
                      {c.title[locale]}
                    </Link>
                  ))}
                </div>
              ))}
              <Link href={p('/courses')} className="mx-3 mt-1 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-red">
                {t.allCourses} <ArrowRight className="h-4 w-4" />
              </Link>
            </MobileGroup>
            <MobileLink href={p('/work-in-japan')} active={isActive('/work-in-japan')}>{t.work}</MobileLink>
            <MobileLink href={p('/jlpt')} active={isActive('/jlpt')}>{t.jlpt}</MobileLink>
            <MobileGroup title={t.programs}>
              {t.programsItems.map((it) => (
                <Link key={it.href} href={p(it.href)} className="block rounded-xl px-3 py-2.5 active:bg-white">
                  <span className="block text-[15px] font-semibold text-brand-navy">{it.title}</span>
                  <span className="block text-xs text-brand-muted">{it.desc}</span>
                </Link>
              ))}
            </MobileGroup>
            <MobileGroup title={t.about}>
              {t.aboutItems.map((it) => (
                <Link key={it.href} href={p(it.href)} className="block rounded-xl px-3 py-2.5 active:bg-white">
                  <span className="block text-[15px] font-semibold text-brand-navy">{it.title}</span>
                  <span className="block text-xs text-brand-muted">{it.desc}</span>
                </Link>
              ))}
            </MobileGroup>
            <MobileLink href={p('/contact')} active={isActive('/contact')}>{t.contact}</MobileLink>

            <div className="mt-6 grid grid-cols-2 gap-2">
              <a href={WHATSAPP} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 rounded-xl bg-[#25D366] py-3 text-sm font-semibold text-white">
                <MessageCircle className="h-4 w-4" /> WhatsApp
              </a>
              <a href={telHref} className="flex items-center justify-center gap-2 rounded-xl border border-brand-cool-gray bg-white py-3 text-sm font-semibold text-brand-navy">
                <Phone className="h-4 w-4" /> {locale === 'ja' ? '電話する' : 'Call us'}
              </a>
            </div>
            <div className="mt-5 flex items-center justify-between rounded-xl border border-brand-cool-gray/70 bg-white px-4 py-3">
              <span className="text-sm font-medium text-brand-charcoal">{t.language}</span>
              <div className="inline-flex rounded-full bg-brand-soft-gray p-0.5">
                {(['en', 'ja'] as const).map((l) => (
                  <button
                    key={l}
                    type="button"
                    onClick={() => switchLang(l)}
                    aria-pressed={locale === l}
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${locale === l ? 'bg-brand-navy text-white' : 'text-brand-charcoal'}`}
                  >
                    {l === 'en' ? 'English' : '日本語'}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-4 rounded-xl bg-brand-navy px-4 py-3 text-xs text-white/80">
              <LiveClocks locale={locale} />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

function MobileLink({ href, active, children }: { href: string; active?: boolean; children: React.ReactNode }) {
  return (
    <Link
      href={href}
      className={`flex items-center justify-between border-b border-brand-cool-gray/60 px-1 py-4 text-lg font-semibold ${
        active ? 'text-brand-green' : 'text-brand-navy'
      }`}
    >
      {children}
      <ArrowRight className="h-4 w-4 text-brand-gold" />
    </Link>
  )
}

function MobileGroup({ title, children, defaultOpen = false }: { title: string; children: React.ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="border-b border-brand-cool-gray/60">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between px-1 py-4 text-lg font-semibold text-brand-navy"
      >
        {title}
        <ChevronDown className={`h-5 w-5 text-brand-gold transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      <div className={`grid transition-all duration-300 ${open ? 'grid-rows-[1fr] pb-3' : 'grid-rows-[0fr]'}`}>
        <div className="overflow-hidden">{children}</div>
      </div>
    </div>
  )
}
